/**
 * Research data sharing ("telemetry").
 *
 * Off by default. A site coordinator enters a setup code (site code + site
 * token issued by the backend) and the staff member accepts the consent
 * screen; only then does the app queue pseudonymised records and send them to
 * the EPFL-hosted API (backend/). Everything is written to a local queue first
 * and flushed when the network allows, so sideloaded devices with patchy
 * connectivity lose nothing.
 *
 * What leaves the device (and nothing else — the backend rejects unknown
 * fields): a random installation id, the session id, site code (derived
 * server-side from the token), language, mode, timestamps (coarsened to the
 * minute by the server), question ids with answer values, computed results,
 * usage events from EVENT_TYPES, and crash messages. Never: participant alias,
 * provider notes, question/answer labels, IP, device identifiers, location.
 */
import {Platform} from 'react-native';
import {version as APP_VERSION} from '../../package.json';
import {AssessmentResultSummary, AssessmentSessionSnapshot} from './Assessment';
import {AppLanguageCode} from './I18n';
import Storage from './Storage';

export const TELEMETRY_CONFIG_STORAGE_KEY = 'adapt-mh.telemetry.config';
export const TELEMETRY_QUEUE_STORAGE_KEY = 'adapt-mh.telemetry.queue';

/** Bump when the consent wording changes materially; stored with each installation. */
export const CONSENT_VERSION = '2026-10';

/** Staff-only phase: every shared screening is a mock screening with a fictitious persona. */
export const SESSIONS_ARE_MOCK = true;

export const DEFAULT_API_BASE_URL = 'https://adapt-mh.epfl.ch';

const MAX_QUEUE_LENGTH = 2000;
const MAX_BATCH_SIZE = 500;
const FLUSH_DEBOUNCE_MS = 1500;
const REQUEST_TIMEOUT_MS = 15000;

// Keep in sync with backend/app/models.py EVENT_TYPES.
export type TelemetryEventType =
  | 'app_open'
  | 'consent_given'
  | 'consent_withdrawn'
  | 'language_changed'
  | 'screening_started'
  | 'screening_resumed'
  | 'question_answered'
  | 'question_back'
  | 'screening_paused'
  | 'screening_completed'
  | 'results_viewed'
  | 'support_viewed'
  | 'export_shared'
  | 'provider_action';

export type TelemetryPayload = Record<string, string | number | boolean>;

export type TelemetryConfig = {
  installId: string;
  siteCode: string;
  siteName: string;
  siteToken: string;
  consentVersion: string;
  consentedAt: string;
  enabled: boolean;
};

export type SiteInfo = {
  siteCode: string;
  name: string;
  country: string;
};

type SessionRecord = {
  kind: 'session';
  sessionId: string;
  language: string;
  mode: AssessmentSessionSnapshot['mode'];
  startedByProvider: boolean | null;
  mock: boolean;
  status: 'active' | 'completed';
  startedAt: string;
  completedAt: string | null;
  answers: {questionId: string; answerValue: string; answeredAt: string}[];
  result: {
    severityLevel: AssessmentResultSummary['severityLevel'];
    topConcernLabel: string;
    maxProbability: number;
    predictions: Record<string, number>;
    moduleVerdicts: {
      target: string;
      label: string;
      criteriaMet: boolean;
      determined: boolean;
      nQuestions: number;
    }[];
    riskFlagTriggered: boolean;
  } | null;
};

type EventRecord = {
  kind: 'event';
  eventId: string;
  eventType: TelemetryEventType;
  sessionId: string | null;
  occurredAt: string;
  language: string | null;
  payload: TelemetryPayload;
};

type CrashRecord = {
  kind: 'crash';
  eventId: string;
  occurredAt: string;
  osVersion: string | null;
  message: string;
  stack: string | null;
};

export type TelemetryRecord = SessionRecord | EventRecord | CrashRecord;

export class TelemetryError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'TelemetryError';
    this.status = status;
  }
}

// ---------------------------------------------------------------------------
// Small helpers

export const getApiBaseUrl = (): string => {
  const fromEnv =
    typeof process !== 'undefined' && process.env ? process.env.ADAPT_MH_API_URL : undefined;
  return (fromEnv && fromEnv.trim()) || DEFAULT_API_BASE_URL;
};

const randomHex = (bytes: number): string => {
  const globalCrypto = (globalThis as {crypto?: {getRandomValues?: (a: Uint8Array) => void}})
    .crypto;
  const buffer = new Uint8Array(bytes);
  if (globalCrypto?.getRandomValues) {
    globalCrypto.getRandomValues(buffer);
  } else {
    for (let i = 0; i < bytes; i += 1) buffer[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(buffer, (b) => b.toString(16).padStart(2, '0')).join('');
};

/** RFC 4122 v4 UUID; the backend requires UUIDs for installation and event ids. */
export const createUuid = (): string => {
  const hex = randomHex(16).split('');
  hex[12] = '4';
  hex[16] = ['8', '9', 'a', 'b'][parseInt(hex[16], 16) % 4];
  const s = hex.join('');
  return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`;
};

const SITE_CODE_PATTERN = /^[A-Z]{2}-[A-Z0-9]{2,12}$/;
const SITE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

/** Setup codes look like `CH-GVA.<token>`; the site code is only for display. */
export const parseSetupCode = (raw: string): {siteCode: string; token: string} | null => {
  const value = raw.trim();
  const dot = value.indexOf('.');
  if (dot <= 0) return null;
  const siteCode = value.slice(0, dot).toUpperCase();
  const token = value.slice(dot + 1);
  if (!SITE_CODE_PATTERN.test(siteCode) || !SITE_TOKEN_PATTERN.test(token)) return null;
  return {siteCode, token};
};

const platformName = (): 'android' | 'ios' | 'web' => {
  if (Platform.OS === 'android' || Platform.OS === 'ios') return Platform.OS;
  return 'web';
};

const osVersion = (): string | null => {
  if (Platform.OS === 'web') return null;
  return String(Platform.Version).slice(0, 64);
};

// ---------------------------------------------------------------------------
// Config

export const loadTelemetryConfig = async (): Promise<TelemetryConfig | null> => {
  const raw = await Storage.get(TELEMETRY_CONFIG_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as TelemetryConfig;
    return parsed && typeof parsed.installId === 'string' ? parsed : null;
  } catch {
    return null;
  }
};

const saveTelemetryConfig = (config: TelemetryConfig): Promise<boolean> =>
  Storage.set(TELEMETRY_CONFIG_STORAGE_KEY, JSON.stringify(config));

export const isSharingEnabled = async (): Promise<boolean> => {
  const config = await loadTelemetryConfig();
  return Boolean(config?.enabled);
};

// ---------------------------------------------------------------------------
// HTTP

const request = async (
  config: Pick<TelemetryConfig, 'siteToken'>,
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<unknown> => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
  const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : undefined;
  try {
    const response = await fetch(`${getApiBaseUrl()}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${config.siteToken}`,
        ...(body !== undefined ? {'Content-Type': 'application/json'} : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller?.signal,
    });
    if (!response.ok) {
      throw new TelemetryError(`Request failed with status ${response.status}`, response.status);
    }
    return await response.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
};

/** Check a setup code against the server. Throws TelemetryError when invalid or offline. */
export const verifySetupCode = async (raw: string): Promise<SiteInfo> => {
  const parsed = parseSetupCode(raw);
  if (!parsed) throw new TelemetryError('Malformed setup code', 400);
  const info = (await request({siteToken: parsed.token}, 'GET', '/v1/site')) as SiteInfo;
  if (!info || info.siteCode !== parsed.siteCode) {
    throw new TelemetryError('Setup code does not match this site', 400);
  }
  return info;
};

// ---------------------------------------------------------------------------
// Queue (serialised through a promise chain so concurrent writers never clobber)

let queueLock: Promise<unknown> = Promise.resolve();

const withQueue = <T>(fn: (queue: TelemetryRecord[]) => Promise<T> | T): Promise<T> => {
  const run = async () => {
    const raw = await Storage.get(TELEMETRY_QUEUE_STORAGE_KEY);
    let queue: TelemetryRecord[] = [];
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        queue = Array.isArray(parsed) ? parsed : [];
      } catch {
        queue = [];
      }
    }
    const before = JSON.stringify(queue);
    const result = await fn(queue);
    const after = JSON.stringify(queue);
    if (after !== before) {
      await Storage.set(TELEMETRY_QUEUE_STORAGE_KEY, after);
    }
    return result;
  };
  const next = queueLock.then(run, run);
  queueLock = next.catch(() => undefined);
  return next;
};

export const getPendingCount = (): Promise<number> => withQueue((queue) => queue.length);

const enqueue = (record: TelemetryRecord): Promise<void> =>
  withQueue((queue) => {
    if (record.kind === 'session') {
      // A session record is the full current state: replace any older copy.
      const index = queue.findIndex(
        (item) => item.kind === 'session' && item.sessionId === record.sessionId,
      );
      if (index >= 0) {
        queue.splice(index, 1);
      }
    }
    queue.push(record);
    if (queue.length > MAX_QUEUE_LENGTH) {
      queue.splice(0, queue.length - MAX_QUEUE_LENGTH);
    }
  });

let flushTimer: ReturnType<typeof setTimeout> | undefined;
let flushing: Promise<void> | undefined;

const recordKey = (record: TelemetryRecord): string =>
  record.kind === 'session' ? `session:${record.sessionId}` : `event:${record.eventId}`;

/** Send one batch; resolves to true when the queue should be re-checked for more. */
const sendNextBatch = async (config: TelemetryConfig): Promise<boolean> => {
  const batch = await withQueue((queue) => queue.slice(0, MAX_BATCH_SIZE));
  if (batch.length === 0) return false;
  try {
    const response = (await request(config, 'POST', '/v1/ingest', {
      installation: {
        installId: config.installId,
        platform: platformName(),
        appVersion: APP_VERSION,
        consentVersion: config.consentVersion,
        consentedAt: config.consentedAt,
      },
      records: batch,
    })) as {rejected?: {index: number; kind: string | null; errors: string[]}[]};
    if (response?.rejected?.length) {
      // The server stored the rest; the rejected records are dropped below with
      // the batch. Field paths only, so this log never contains answer data.
      console.warn('Telemetry records rejected by server', JSON.stringify(response.rejected));
    }
  } catch (error) {
    if (error instanceof TelemetryError && (error.status === 422 || error.status === 413)) {
      // The server will never accept this batch; drop it rather than retry forever.
      console.warn('Telemetry batch rejected by server, dropping', error.message);
    } else {
      return false; // offline, 401 (site revoked), 5xx: keep for next time
    }
  }
  const sentKeys = new Set(batch.map(recordKey));
  const sentJson = new Set(batch.map((record) => JSON.stringify(record)));
  await withQueue((queue) => {
    for (let i = queue.length - 1; i >= 0; i -= 1) {
      const record = queue[i];
      // A session re-queued (updated) while in flight keeps its newer copy.
      if (
        sentKeys.has(recordKey(record)) &&
        (record.kind !== 'session' || sentJson.has(JSON.stringify(record)))
      ) {
        queue.splice(i, 1);
      }
    }
  });
  return true;
};

export const flushTelemetry = async (): Promise<void> => {
  if (flushing) return flushing;
  flushing = (async () => {
    const config = await loadTelemetryConfig();
    if (!config?.enabled) return;
    const drain = async (): Promise<void> => {
      if (await sendNextBatch(config)) await drain();
    };
    await drain();
  })().finally(() => {
    flushing = undefined;
  });
  return flushing;
};

const scheduleFlush = () => {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = undefined;
    flushTelemetry().catch(() => undefined);
  }, FLUSH_DEBOUNCE_MS);
};

// ---------------------------------------------------------------------------
// Public recording API (all no-ops unless sharing is enabled)

export const trackEvent = async (
  eventType: TelemetryEventType,
  options: {sessionId?: string; language?: AppLanguageCode; payload?: TelemetryPayload} = {},
): Promise<void> => {
  try {
    if (!(await isSharingEnabled())) return;
    await enqueue({
      kind: 'event',
      eventId: createUuid(),
      eventType,
      sessionId: options.sessionId ?? null,
      occurredAt: new Date().toISOString(),
      language: options.language ?? null,
      payload: options.payload ?? {},
    });
    scheduleFlush();
  } catch (error) {
    console.warn('Telemetry event not recorded', error);
  }
};

/** Pseudonymised view of a screening session. Exported for tests. */
export const toSessionRecord = (
  snapshot: AssessmentSessionSnapshot,
  summary?: AssessmentResultSummary,
): SessionRecord => ({
  kind: 'session',
  sessionId: snapshot.sessionId,
  language: snapshot.language,
  mode: snapshot.mode,
  startedByProvider: snapshot.startedByProvider ?? null,
  mock: SESSIONS_ARE_MOCK,
  status: snapshot.status,
  startedAt: snapshot.createdAt,
  completedAt: summary?.completedAt ?? snapshot.resultSummary?.completedAt ?? null,
  answers: snapshot.answers.map((answer) => ({
    questionId: answer.questionId,
    answerValue: answer.answerValue,
    answeredAt: answer.answeredAt,
  })),
  result: (() => {
    const result = summary ?? snapshot.resultSummary;
    if (!result) return null;
    return {
      severityLevel: result.severityLevel,
      topConcernLabel: result.topConcernLabel,
      maxProbability: result.maxProbability,
      predictions: result.predictions,
      moduleVerdicts: (result.moduleVerdicts ?? []).map((verdict) => ({
        target: verdict.target,
        label: verdict.label,
        criteriaMet: verdict.criteriaMet,
        determined: verdict.determined,
        nQuestions: verdict.nQuestions,
      })),
      riskFlagTriggered: result.riskFlag.triggered,
    };
  })(),
});

export const recordSession = async (
  snapshot: AssessmentSessionSnapshot,
  summary?: AssessmentResultSummary,
): Promise<void> => {
  try {
    if (!(await isSharingEnabled())) return;
    await enqueue(toSessionRecord(snapshot, summary));
    scheduleFlush();
  } catch (error) {
    console.warn('Telemetry session not recorded', error);
  }
};

export const recordCrash = async (error: unknown): Promise<void> => {
  try {
    if (!(await isSharingEnabled())) return;
    const err = error instanceof Error ? error : new Error(String(error));
    await enqueue({
      kind: 'crash',
      eventId: createUuid(),
      occurredAt: new Date().toISOString(),
      osVersion: osVersion(),
      message: `${err.name}: ${err.message}`.slice(0, 1000),
      stack: err.stack ? err.stack.slice(0, 8000) : null,
    });
    scheduleFlush();
  } catch (reportError) {
    console.warn('Telemetry crash not recorded', reportError);
  }
};

// ---------------------------------------------------------------------------
// Consent lifecycle

export const enableSharing = async (rawSetupCode: string): Promise<TelemetryConfig> => {
  const info = await verifySetupCode(rawSetupCode);
  const parsed = parseSetupCode(rawSetupCode);
  if (!parsed) throw new TelemetryError('Malformed setup code', 400);
  const existing = await loadTelemetryConfig();
  const config: TelemetryConfig = {
    // Keep the installation id across re-consent so deletion covers everything.
    installId: existing?.installId ?? createUuid(),
    siteCode: info.siteCode,
    siteName: info.name,
    siteToken: parsed.token,
    consentVersion: CONSENT_VERSION,
    consentedAt: new Date().toISOString(),
    enabled: true,
  };
  await saveTelemetryConfig(config);
  await trackEvent('consent_given', {payload: {consentVersion: CONSENT_VERSION}});
  await flushTelemetry().catch(() => undefined);
  return config;
};

export const disableSharing = async (): Promise<void> => {
  const config = await loadTelemetryConfig();
  if (!config) return;
  await trackEvent('consent_withdrawn');
  await flushTelemetry().catch(() => undefined);
  await saveTelemetryConfig({...config, enabled: false});
};

export type DeletionResult = {sessions: number; events: number; crashes: number};

/** Ask the server to delete everything tied to this installation, then forget it locally. */
export const deleteSharedData = async (): Promise<DeletionResult> => {
  const config = await loadTelemetryConfig();
  if (!config) {
    await Storage.remove(TELEMETRY_QUEUE_STORAGE_KEY);
    return {sessions: 0, events: 0, crashes: 0};
  }
  const response = (await request(config, 'DELETE', `/v1/installations/${config.installId}`)) as {
    deleted: DeletionResult;
  };
  await withQueue((queue) => {
    queue.splice(0, queue.length);
  });
  await Storage.remove(TELEMETRY_CONFIG_STORAGE_KEY);
  return response.deleted;
};

// ---------------------------------------------------------------------------
// App-level wiring

let handlersInstalled = false;

/** Install crash reporting hooks once per app process. Safe to call repeatedly. */
export const installTelemetryHandlers = (): void => {
  if (handlersInstalled) return;
  handlersInstalled = true;

  if (Platform.OS === 'web') {
    const win = globalThis as unknown as {
      addEventListener?: (type: string, listener: (event: unknown) => void) => void;
    };
    win.addEventListener?.('error', (event) => {
      const err = (event as {error?: unknown; message?: string}).error ?? event;
      recordCrash(
        err instanceof Error ? err : new Error(String((event as {message?: string}).message)),
      );
    });
    win.addEventListener?.('unhandledrejection', (event) => {
      recordCrash((event as {reason?: unknown}).reason ?? new Error('Unhandled rejection'));
    });
    return;
  }

  const errorUtils = (
    globalThis as unknown as {
      ErrorUtils?: {
        getGlobalHandler: () => (error: Error, isFatal?: boolean) => void;
        setGlobalHandler: (handler: (error: Error, isFatal?: boolean) => void) => void;
      };
    }
  ).ErrorUtils;
  if (!errorUtils) return;
  const previous = errorUtils.getGlobalHandler();
  errorUtils.setGlobalHandler((error, isFatal) => {
    recordCrash(error).finally(() => previous?.(error, isFatal));
  });
};

/** Reset module state between tests. */
export const __resetTelemetryForTests = (): void => {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = undefined;
  flushing = undefined;
  queueLock = Promise.resolve();
  handlersInstalled = false;
};
